import React, { useEffect } from 'react';
import { Spin, Pagination, message, Form, Input, Rate, Dropdown, List } from 'antd';
import { SendOutlined, FileOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { Container } from '@/components/ui/Container';
import { Button } from '@/components/ui/Button';
import { SliderTab } from '@/components/ui/SliderTab';
import RichTextEditor from '@/components/ui/RichTextEditor';
import StatusBadge from '@/components/ui/StatusBadge';
import Markdown from '@/components/ui/Markdown';
import AppModal from '../AppModal';
import PMOKanbanBoard from '@/components/module/projects/pmo_tab/PMOKanbanBoard';
import PmoSubmitToClientModal from '@/components/module/projects/pmo_tab/PmoSubmitToClientModal';
import TaskDetailModal from '@/components/module/projects/task/TaskDetailModal';
import MilestoneLinkModal from '@/components/module/projects/milestone_tab/MilestoneLinkModal';
import { useProjectPMO, isSubmissionPending } from '@hooks/projects/useProjectPMO';
import { formatDate, getMilestoneDisplayLabel, groupItemsByMilestoneLabel } from '@hooks/projects/projectHelpers';
import { PendingAttachmentTile, PendingLinkWrap } from '@/components/module/projects/sprints_tab/PendingRichTextAttachments';
import PhaseDescriptionAttachments from '@/components/module/projects/milestone_tab/PhaseDescriptionAttachments';
import {
    EmptyStateBlock,
    EmptyStateIcon,
    EmptyStateTitle,
    EmptyStateDescription,
    DetailPanelLoading,
    MutedText,
    CardGridScroll,
    CardWithMinHeight,
    ContentCardTitle,
    PanelDivider,
    PanelCardHeading,
    CreateRoleModalBody,
    CreateRoleModalFooter,
    BoardKanbanBoardShell,
    BoardTabRoot,
    BoardPageTitle,
    BoardPageSubtitle,
    BoardUnderlineTabRow,
    BoardUnderlineTabInner,
    BoardUnderlineTab,
    BoardUnderlineTabLine,
    BoardUnderlineTabPrimaryLabel,
    PanelSectionLabel,
    RolesSliderTabWrap,
} from '../style';
import { truncateFileName } from '@hooks/projects/projectHelpers';

export default function ProjectPMOTab({ projectId, project }) {
    const {
        clientSubmissions,
        total,
        page,
        limit,
        totalPages,
        isLoading,
        isClientView,
        canSubmitToClient,
        canRespondToRequest,
        canCreateChangeRequest,
        pmoViewTab,
        setPmoViewTab,
        submissionColumn,
        submittedToClientColumn,
        submitToClientPending,
        submitModalOpen,
        selectedSubmission,
        submissionDetailModalOpen,
        notes,
        setNotes,
        submitModalPresentation,
        pmoSubmitModalTaskRows,
        submitModalSubmissionDescriptionRaw,
        submitModalDetailSections,
        submissionDetailPresentation,
        pmoSubmissionDetailTaskRows,
        submissionDetailDescriptionRaw,
        submissionDetailViewLinks,
        submissionDetailViewAttachments,
        submissionDetailViewClientAttachments,
        submissionDetailSections,
        pendingLinks,
        pendingFiles,
        closeSubmitModal,
        closeSubmissionDetailModal,
        handleFilesSelected,
        handleRemoveAttachment,
        handleRemoveLink,
        handleLinkRequest,
        linkModalOpen,
        linkForm,
        closeLinkModal,
        handleLinkModalSubmit,
        handleSubmitToClient,
        respondModalOpen,
        respondAction,
        respondResponseTab,
        setRespondResponseTab,
        respondNote,
        setRespondNote,
        respondRating,
        setRespondRating,
        respondPendingLinks,
        respondPendingFiles,
        openRespondModal,
        closeRespondModal,
        handleRespondFilesSelected,
        handleRespondRemoveAttachment,
        handleRespondRemoveLink,
        handleRespondEditorLinkRemoved,
        handleRespondLinkRequest,
        respondLinkModalOpen,
        respondLinkForm,
        closeRespondLinkModal,
        handleRespondLinkModalSubmit,
        handleRespondSubmit,
        respondPending,
        changeRequestForm,
        submitChangeRequest,
        createChangeRequestPending,
        clientFeedbackItems,
        changeRequestPendingLinks,
        changeRequestPendingFiles,
        handleChangeRequestFilesSelected,
        handleChangeRequestRemoveAttachment,
        handleChangeRequestRemoveLink,
        handleChangeRequestEditorLinkRemoved,
        changeRequestLinkModalOpen,
        changeRequestLinkForm,
        closeChangeRequestLinkModal,
        handleChangeRequestLinkModalSubmit,
        handleChangeRequestLinkRequest,
        selectedTask,
        taskDescriptionMarkdown,
        pmoTaskDetailBugs,
        handleTaskSelect,
        handleSubmissionKanbanOpen,
        handleTaskFromSubmissionKanban,
        handleTaskFromSubmittedClientKanban,
        handleSubmittedClientKanbanOpen,
        closeTaskPanel,
        clientRequestDetailOpen,
        clientRequestDetail,
        closeClientRequestDetail,
        openClientRequestDetail,
        clientRequestDetailPresentation,
        clientRequestDetailNotesRaw,
        clientRequestDetailViewAttachments,
        clientRequestDetailViewClientAttachments,
        clientRequestDetailSections,
        mentionUsers,
        mentionMilestones,
        mentionSprints,
        dropTarget,
        draggingColumn,
        handleDndDragStart,
        handleDndDragOverColumn,
        handleDndDragEnd,
        clearDropTarget,
        onPageChange,
        setPage,
    } = useProjectPMO(projectId, project);

    const changeRequestDescriptionValue = Form.useWatch('description', changeRequestForm) ?? '';

    useEffect(() => {
        setPage(1);
    }, [pmoViewTab, setPage]);

    const submitModalAfterDescription =
        pendingFiles.length > 0 || pendingLinks.length > 0 ? (
            <div
                style={{
                    marginTop: 8,
                    marginBottom: 16,
                    padding: '8px 0',
                    borderTop: '1px solid var(--ant-color-border, #f0f0f0)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0,
                }}
            >
                {pendingFiles.length > 0 ? (
                    <>
                        <PanelSectionLabel
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 6,
                                marginBottom: 8,
                                fontSize: 11,
                                textTransform: 'uppercase',
                                letterSpacing: '0.04em',
                                opacity: 0.75,
                            }}
                        >
                            <FileOutlined />
                            Attachments
                        </PanelSectionLabel>
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
                                gap: 10,
                            }}
                        >
                            {pendingFiles.map((p) => (
                                <PendingAttachmentTile
                                    key={p.id}
                                    item={p}
                                    onRemove={() => handleRemoveAttachment(p.id)}
                                    truncateFileName={truncateFileName}
                                    showBottomFileName={false}
                                />
                            ))}
                        </div>
                    </>
                ) : null}
                {pendingLinks.length > 0 ? (
                    <div style={{ marginTop: pendingFiles.length > 0 ? 12 : 0 }}>
                        <PendingLinkWrap links={pendingLinks} onRemoveLink={handleRemoveLink} />
                    </div>
                ) : null}
            </div>
        ) : null;

    if (!projectId) {
        return (
            <EmptyStateBlock>
                <DetailPanelLoading>
                    <Spin size="large" />
                </DetailPanelLoading>
            </EmptyStateBlock>
        );
    }

    const mainTabs = (
        <BoardUnderlineTabRow $sticky style={{ marginBottom: 20, flexShrink: 0 }}>
            <BoardUnderlineTabInner>
                <BoardUnderlineTab
                    type="button"
                    $active={pmoViewTab === 'submission'}
                    onClick={() => setPmoViewTab('submission')}
                >
                    <BoardUnderlineTabLine>
                        <BoardUnderlineTabPrimaryLabel $active={pmoViewTab === 'submission'}>
                            Submission
                        </BoardUnderlineTabPrimaryLabel>
                    </BoardUnderlineTabLine>
                </BoardUnderlineTab>
                <BoardUnderlineTab type="button" $active={pmoViewTab === 'feedback'} onClick={() => setPmoViewTab('feedback')}>
                    <BoardUnderlineTabLine>
                        <BoardUnderlineTabPrimaryLabel $active={pmoViewTab === 'feedback'}>
                            Client Feedback
                        </BoardUnderlineTabPrimaryLabel>
                    </BoardUnderlineTabLine>
                </BoardUnderlineTab>
            </BoardUnderlineTabInner>
        </BoardUnderlineTabRow>
    );

    const pendingClientSubmissions = (clientSubmissions ?? []).filter(
        (x) => String(x?.status ?? '').toLowerCase() === 'pending',
    );
    const pendingClientSubmissionGroups = groupItemsByMilestoneLabel(pendingClientSubmissions);

    const clientRequestsList =
        pendingClientSubmissions.length === 0 ? (
            <EmptyStateBlock>
                <EmptyStateIcon>
                    <SendOutlined />
                </EmptyStateIcon>
                <EmptyStateTitle>No pending completion requests</EmptyStateTitle>
                <EmptyStateDescription>Pending completion requests sent by the team will appear here.</EmptyStateDescription>
            </EmptyStateBlock>
        ) : (
            <Container $display="flex" $flexDirection="column" $gap="16px" $minHeight="0" $flex="1">
                <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 12 }}>
                    {pendingClientSubmissionGroups.map(({ label, items }) => (
                        <div key={`pending-${label}`} style={{ marginBottom: 16 }}>
                            <div style={{ margin: '0 0 10px 0' }}>
                                <PanelCardHeading>{label}</PanelCardHeading>
                                <PanelDivider style={{ marginTop: 8, paddingTop: 8 }} />
                            </div>
                            <List
                                dataSource={items}
                                grid={{
                                    gutter: 16,
                                    xs: 1,
                                    sm: 1,
                                    md: 2,
                                    lg: 2,
                                    xl: 3,
                                    xxl: 3,
                                }}
                                renderItem={(sub) => (
                                    <List.Item key={sub.id} style={{ margin: 0, padding: '0 0 16px 0' }}>
                                        <CardWithMinHeight
                                            $padding="16px"
                                            $minHeight="180px"
                                            $noHoverShadow
                                            style={{
                                                height: 450,
                                                maxHeight: 450,
                                                overflow: 'hidden',
                                            }}
                                        >
                                            <div
                                                role="button"
                                                tabIndex={0}
                                                onClick={() => openClientRequestDetail(sub)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        e.preventDefault();
                                                        openClientRequestDetail(sub);
                                                    }
                                                }}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'flex-start',
                                                    justifyContent: 'space-between',
                                                    gap: 8,
                                                    marginBottom: 8,
                                                    cursor: 'pointer',
                                                }}
                                            >
                                                <ContentCardTitle $marginBottom="0" style={{ flex: 1, minWidth: 0 }}>
                                                    {getMilestoneDisplayLabel(sub?.milestone)}
                                                </ContentCardTitle>
                                                {sub?.status != null && <StatusBadge status={sub.status} size="compact" />}
                                            </div>

                                            <PanelDivider />

                                            <div
                                                onClick={(e) => e.stopPropagation()}
                                                style={{
                                                    flex: 1,
                                                    minHeight: 0,
                                                    overflowY: 'hidden',
                                                    paddingRight: 4,
                                                }}
                                            >
                                                <MutedText style={{ fontSize: 12 }}>
                                                    Requested {formatDate(sub?.requestedAt ?? sub?.createdAt) ?? '—'}
                                                </MutedText>
                                                {sub?.requestedByName && (
                                                    <>
                                                        <PanelDivider />
                                                        <MutedText style={{ fontSize: 12 }}>Requested by: {sub.requestedByName}</MutedText>
                                                    </>
                                                )}
                                                {(() => {
                                                    const raw =
                                                        sub?.completionRequestNotes?.markdown ??
                                                        sub?.completionRequestNotes ??
                                                        sub?.requestNotes?.markdown ??
                                                        sub?.requestNotes ??
                                                        '';
                                                    const text = typeof raw === 'string' ? raw.trim() : '';
                                                    const items = [
                                                        ...(Array.isArray(sub?.completionRequestLinks) ? sub.completionRequestLinks : []),
                                                        ...(Array.isArray(sub?.completionRequestAttachments) ? sub.completionRequestAttachments : []),
                                                    ];
                                                    const fallbackItems = Array.isArray(sub?.requestAttachments) ? sub.requestAttachments : [];
                                                    const attachmentItems = items.length > 0 ? items : fallbackItems;
                                                    return (
                                                        <>
                                                            <PanelDivider />
                                                            <div
                                                                style={{
                                                                    border: '1px solid var(--ant-color-border-secondary, #f0f0f0)',
                                                                    borderRadius: 10,
                                                                    padding: '8px 10px',
                                                                    background: 'var(--ant-color-fill-quaternary, rgba(0,0,0,0.02))',
                                                                }}
                                                            >
                                                                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
                                                                    <div style={{ minWidth: 0, flex: 1 }}>
                                                                        <div
                                                                            style={{
                                                                                fontSize: 11,
                                                                                fontWeight: 700,
                                                                                letterSpacing: '0.04em',
                                                                                textTransform: 'uppercase',
                                                                                color: 'var(--ant-color-text-secondary, rgba(0,0,0,0.55))',
                                                                                marginBottom: 4,
                                                                                fontFamily: 'var(--font-family-geist)',
                                                                            }}
                                                                        >
                                                                            Request notes
                                                                        </div>
                                                                        {text ? (
                                                                            <div style={{ height: 66, overflow: 'hidden' }}>
                                                                                <Markdown
                                                                                    source={text}
                                                                                    variant="compact"
                                                                                    lineClamp={3}
                                                                                    linkPrimary
                                                                                    richInlineAnchors
                                                                                    attachmentItems={attachmentItems}
                                                                                />
                                                                            </div>
                                                                        ) : (
                                                                            <div style={{ fontSize: 13, color: 'var(--ant-color-text-secondary, rgba(0,0,0,0.55))' }}>—</div>
                                                                        )}
                                                                    </div>
                                                                    {attachmentItems.length > 0 ? (
                                                                        <div style={{ flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                                                                            <PhaseDescriptionAttachments
                                                                                items={attachmentItems}
                                                                                style={{ marginTop: 0 }}
                                                                                hideFileNames
                                                                            />
                                                                        </div>
                                                                    ) : null}
                                                                </div>
                                                            </div>
                                                        </>
                                                    );
                                                })()}
                                            </div>

                                            {String(sub?.status || '').toLowerCase() === 'pending' && (
                                                <>
                                                    <PanelDivider />
                                                    <Container
                                                        $display="flex"
                                                        $justifyContent="flex-end"
                                                        $alignItems="center"
                                                        $gap="10px"
                                                        style={{ marginTop: 4 }}
                                                    >
                                                        <Container $display="flex" $gap="8px" $flexWrap="nowrap" style={{ alignItems: 'center' }}>
                                                            <Button
                                                                type="button"
                                                                $primary
                                                                $blackOnLight
                                                                $size="12px"
                                                                $padding="6px 12px"
                                                                icon={<CheckOutlined />}
                                                                loading={respondPending}
                                                                disabled={respondPending}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    openRespondModal(sub.id, 'accepted');
                                                                }}
                                                            >
                                                                Approve
                                                            </Button>
                                                            <Button
                                                                type="button"
                                                                $dangerOutline
                                                                $primary={false}
                                                                $size="12px"
                                                                $padding="6px 12px"
                                                                icon={<CloseOutlined />}
                                                                loading={respondPending}
                                                                disabled={respondPending}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    openRespondModal(sub.id, 'declined');
                                                                }}
                                                            >
                                                                Reject
                                                            </Button>
                                                        </Container>
                                                    </Container>
                                                </>
                                            )}
                                        </CardWithMinHeight>
                                    </List.Item>
                                )}
                            />
                        </div>
                    ))}
                </div>
                {totalPages > 1 && pendingClientSubmissions.length > 0 && (
                    <Container $display="flex" $justifyContent="flex-end" $flexShrink="0">
                        <Pagination
                            current={page}
                            pageSize={limit}
                            total={total}
                            onChange={onPageChange}
                            showSizeChanger
                            showTotal={(t, range) => `${range[0]}-${range[1]} of ${t}`}
                        />
                    </Container>
                )}
            </Container>
        );

    const clientFeedbackView = !isClientView ? null : (() => {
        const completionResponses = (clientFeedbackItems?.completionResponses ?? []).filter(
            (sub) => ['accepted', 'declined', 'change-request'].includes(String(sub?.status ?? '').toLowerCase())
        );
        const changeRequests = (clientFeedbackItems?.changeRequests ?? []).filter(
            (cr) => ['accepted', 'declined'].includes(String(cr?.status ?? '').toLowerCase())
        );
        const isEmpty = completionResponses.length === 0 && changeRequests.length === 0;
        return (
            <Container $display="flex" $flexDirection="column" $flex="1" $minHeight="0" style={{ minHeight: 0 }}>
                {isEmpty ? (
                    <EmptyStateBlock>
                        <EmptyStateIcon>
                            <SendOutlined />
                        </EmptyStateIcon>
                        <EmptyStateTitle>No client feedback yet</EmptyStateTitle>
                        <EmptyStateDescription>
                            Approvals, rejections, and change requests will appear here once you respond to a submission.
                        </EmptyStateDescription>
                    </EmptyStateBlock>
                ) : (
                    <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 12 }}>
                        {groupItemsByMilestoneLabel([...completionResponses, ...changeRequests]).map(({ label, items }) => (
                            <div key={`feedback-${label}`} style={{ marginBottom: 16 }}>
                                <div style={{ margin: '0 0 10px 0' }}>
                                    <PanelCardHeading>{label}</PanelCardHeading>
                                    <PanelDivider style={{ marginTop: 8, paddingTop: 8 }} />
                                </div>
                                <List
                                    dataSource={items}
                                    grid={{
                                        gutter: 16,
                                        xs: 1,
                                        sm: 1,
                                        md: 1,
                                        lg: 1,
                                        xl: 2,
                                        xxl: 3,
                                    }}
                                    renderItem={(item) => {
                                        const isCompletionResponse =
                                            Object.prototype.hasOwnProperty.call(item ?? {}, 'respondedAt') ||
                                            Object.prototype.hasOwnProperty.call(item ?? {}, 'clientNote') ||
                                            Object.prototype.hasOwnProperty.call(item ?? {}, 'rating') ||
                                            Object.prototype.hasOwnProperty.call(item ?? {}, 'clientRespondedAt') ||
                                            // Change-request completion items can be missing respondedAt/clientNote/rating.
                                            // Treat anything that looks like a completion request row as a "completion response" card.
                                            Object.prototype.hasOwnProperty.call(item ?? {}, 'submissionId') ||
                                            Object.prototype.hasOwnProperty.call(item ?? {}, 'submissionType') ||
                                            Object.prototype.hasOwnProperty.call(item ?? {}, 'requestedAt');

                                        if (isCompletionResponse) {
                                            const status = String(item?.status ?? '').toLowerCase();
                                            const isAccepted = status === 'accepted';
                                            const isDeclined = status === 'declined';
                                            const isChangeRequest = status === 'change-request';
                                            const respondedAt = item?.clientRespondedAt ?? item?.respondedAt ?? item?.updatedAt ?? item?.createdAt;
                                            const requestedAt = item?.requestedAt ?? item?.createdAt;
                                            const note =
                                                status === 'change-request'
                                                    ? item?.notes?.markdown ?? item?.content ?? ''
                                                    : item?.clientNote?.markdown ?? item?.clientNote ?? item?.note ?? '';
                                            const rating = item?.rating ?? item?.clientRating ?? null;
                                            const requestedByName = item?.requestedByName;
                                            const responseAttachments =
                                                status === 'change-request' && Array.isArray(item?.attachments) ? item.attachments : item?.clientAttachments;

                                            return (
                                                <List.Item
                                                    key={`resp-${item?.id ?? respondedAt ?? Math.random()}`}
                                                    style={{ margin: 0, padding: '0 0 16px 0' }}
                                                >
                                                    <CardWithMinHeight
                                                        $padding="16px"
                                                        $minHeight="180px"
                                                        $noHoverShadow
                                                        style={{ height: 460, maxHeight: 460, overflow: 'hidden' }}
                                                    >
                                                        <div
                                                            role="button"
                                                            tabIndex={0}
                                                            onClick={() => openClientRequestDetail(item)}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter' || e.key === ' ') {
                                                                    e.preventDefault();
                                                                    openClientRequestDetail(item);
                                                                }
                                                            }}
                                                            style={{
                                                                display: 'flex',
                                                                alignItems: 'flex-start',
                                                                justifyContent: 'space-between',
                                                                gap: 8,
                                                                marginBottom: 8,
                                                                cursor: 'pointer',
                                                            }}
                                                        >
                                                            <ContentCardTitle $marginBottom="0" style={{ flex: 1, minWidth: 0 }}>
                                                                {getMilestoneDisplayLabel(item?.milestone)}
                                                            </ContentCardTitle>
                                                            {item?.status != null && <StatusBadge status={item.status} size="compact" />}
                                                        </div>

                                                        <PanelDivider />

                                                        <div
                                                            onClick={(e) => e.stopPropagation()}
                                                            style={{ flex: 1, minHeight: 0, overflowY: 'hidden', paddingRight: 4 }}
                                                        >
                                                            <MutedText style={{ fontSize: 12 }}>
                                                                Requested {formatDate(requestedAt) ?? '—'}
                                                            </MutedText>
                                                            {requestedByName && (
                                                                <>
                                                                    <PanelDivider />
                                                                    <MutedText style={{ fontSize: 12 }}>Requested by: {requestedByName}</MutedText>
                                                                </>
                                                            )}
                                                            {(() => {
                                                                const raw =
                                                                    item?.completionRequestNotes?.markdown ??
                                                                    item?.completionRequestNotes ??
                                                                    item?.requestNotes?.markdown ??
                                                                    item?.requestNotes ??
                                                                    '';
                                                                const text = typeof raw === 'string' ? raw.trim() : '';
                                                                const reqItems = [
                                                                    ...(Array.isArray(item?.completionRequestLinks) ? item.completionRequestLinks : []),
                                                                    ...(Array.isArray(item?.completionRequestAttachments) ? item.completionRequestAttachments : []),
                                                                ];
                                                                const fallbackReqItems = Array.isArray(item?.requestAttachments) ? item.requestAttachments : [];
                                                                const requestAttachmentItems = reqItems.length > 0 ? reqItems : fallbackReqItems;
                                                                return (
                                                                    <>
                                                                        <PanelDivider />
                                                                        <div
                                                                            style={{
                                                                                border: '1px solid var(--ant-color-border-secondary, #f0f0f0)',
                                                                                borderRadius: 10,
                                                                                padding: '8px 10px',
                                                                                background: 'var(--ant-color-fill-quaternary, rgba(0,0,0,0.02))',
                                                                            }}
                                                                        >
                                                                            <div
                                                                                style={{
                                                                                    display: 'flex',
                                                                                    alignItems: 'flex-start',
                                                                                    justifyContent: 'space-between',
                                                                                    gap: 10,
                                                                                }}
                                                                            >
                                                                                <div style={{ minWidth: 0, flex: 1 }}>
                                                                                    <div
                                                                                        style={{
                                                                                            fontSize: 11,
                                                                                            fontWeight: 700,
                                                                                            letterSpacing: '0.04em',
                                                                                            textTransform: 'uppercase',
                                                                                            color: 'var(--ant-color-text-secondary, rgba(0,0,0,0.55))',
                                                                                            marginBottom: 4,
                                                                                            fontFamily: 'var(--font-family-geist)',
                                                                                        }}
                                                                                    >
                                                                                        Request notes
                                                                                    </div>
                                                                                    {text ? (
                                                                                        <div style={{ height: 66, overflow: 'hidden' }}>
                                                                                            <Markdown
                                                                                                source={text}
                                                                                                variant="compact"
                                                                                                lineClamp={3}
                                                                                                linkPrimary
                                                                                                richInlineAnchors
                                                                                                attachmentItems={requestAttachmentItems}
                                                                                            />
                                                                                        </div>
                                                                                    ) : (
                                                                                        <div style={{ fontSize: 13, color: 'var(--ant-color-text-secondary, rgba(0,0,0,0.55))' }}>—</div>
                                                                                    )}
                                                                                </div>
                                                                                {requestAttachmentItems.length > 0 ? (
                                                                                    <div style={{ flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                                                                                        <PhaseDescriptionAttachments
                                                                                            items={requestAttachmentItems}
                                                                                            style={{ marginTop: 0 }}
                                                                                            hideFileNames
                                                                                        />
                                                                                    </div>
                                                                                ) : null}
                                                                            </div>
                                                                        </div>
                                                                    </>
                                                                );
                                                            })()}

                                                            <PanelDivider />

                                                            <MutedText style={{ fontSize: 12 }}>
                                                                {isAccepted ? 'Approved' : isDeclined ? 'Rejected' : isChangeRequest ? 'Change request' : 'Updated'}{' '}
                                                                {respondedAt ? `· ${formatDate(respondedAt)}` : ''}
                                                            </MutedText>
                                                            {typeof rating === 'number' && (
                                                                <>
                                                                    <PanelDivider />
                                                                    <Rate
                                                                        disabled
                                                                        value={Math.max(1, Math.min(5, Math.round(rating)))}
                                                                        count={5}
                                                                        style={{ fontSize: 14 }}
                                                                    />
                                                                </>
                                                            )}
                                                            {note && !isChangeRequest ? (
                                                                <>
                                                                    <PanelDivider />
                                                                    <MutedText style={{ fontSize: 12, whiteSpace: 'pre-wrap' }}>{note}</MutedText>
                                                                </>
                                                            ) : null}

                                                            {!isChangeRequest &&
                                                                Array.isArray(responseAttachments) &&
                                                                responseAttachments.length > 0 && (
                                                                    <>
                                                                        <PanelDivider />
                                                                        <PhaseDescriptionAttachments
                                                                            items={responseAttachments}
                                                                            style={{ marginTop: 0 }}
                                                                            hideFileNames
                                                                            triggerLabel="Response attachments"
                                                                        />
                                                                    </>
                                                                )}

                                                            {isChangeRequest
                                                                ? (() => {
                                                                    const raw =
                                                                        item?.changeRequestNotes?.markdown ??
                                                                        item?.notes?.markdown ??
                                                                        item?.notes ??
                                                                        item?.content ??
                                                                        '';
                                                                    const text = typeof raw === 'string' ? raw.trim() : '';
                                                                    const items = Array.isArray(item?.changeRequestAttachments)
                                                                        ? item.changeRequestAttachments
                                                                        : Array.isArray(item?.attachments)
                                                                            ? item.attachments
                                                                            : [];
                                                                    return (
                                                                        <>
                                                                            <PanelDivider />
                                                                            <div
                                                                                style={{
                                                                                    border: '1px solid var(--ant-color-border-secondary, #f0f0f0)',
                                                                                    borderRadius: 10,
                                                                                    padding: '8px 10px',
                                                                                    background:
                                                                                        'var(--ant-color-fill-quaternary, rgba(0,0,0,0.02))',
                                                                                }}
                                                                            >
                                                                                <div
                                                                                    style={{
                                                                                        display: 'flex',
                                                                                        alignItems: 'flex-start',
                                                                                        justifyContent: 'space-between',
                                                                                        gap: 10,
                                                                                    }}
                                                                                >
                                                                                    <div style={{ minWidth: 0, flex: 1 }}>
                                                                                        <div
                                                                                            style={{
                                                                                                fontSize: 11,
                                                                                                fontWeight: 700,
                                                                                                letterSpacing: '0.04em',
                                                                                                textTransform: 'uppercase',
                                                                                                color:
                                                                                                    'var(--ant-color-text-secondary, rgba(0,0,0,0.55))',
                                                                                                marginBottom: 4,
                                                                                                fontFamily: 'var(--font-family-geist)',
                                                                                            }}
                                                                                        >
                                                                                            Change request notes
                                                                                        </div>
                                                                                        {text ? (
                                                                                            <div style={{ height: 66, overflow: 'hidden' }}>
                                                                                                <Markdown
                                                                                                    source={text}
                                                                                                    variant="compact"
                                                                                                    lineClamp={3}
                                                                                                    linkPrimary
                                                                                                    richInlineAnchors
                                                                                                    attachmentItems={items}
                                                                                                />
                                                                                            </div>
                                                                                        ) : (
                                                                                            <div
                                                                                                style={{
                                                                                                    fontSize: 13,
                                                                                                    color:
                                                                                                        'var(--ant-color-text-secondary, rgba(0,0,0,0.55))',
                                                                                                }}
                                                                                            >
                                                                                                —
                                                                                            </div>
                                                                                        )}
                                                                                    </div>
                                                                                    {items.length > 0 ? (
                                                                                        <div
                                                                                            style={{ flexShrink: 0 }}
                                                                                            onClick={(e) => e.stopPropagation()}
                                                                                        >
                                                                                            <PhaseDescriptionAttachments
                                                                                                items={items}
                                                                                                style={{ marginTop: 0 }}
                                                                                                hideFileNames
                                                                                                triggerLabel="Attachments"
                                                                                            />
                                                                                        </div>
                                                                                    ) : null}
                                                                                </div>
                                                                            </div>
                                                                        </>
                                                                    );
                                                                })()
                                                                : null}
                                                        </div>
                                                    </CardWithMinHeight>
                                                </List.Item>
                                            );
                                        }

                                        const createdAt = item?.createdAt ?? item?.updatedAt;
                                        const descriptionMarkdown = item?.description?.markdown ?? item?.description ?? '';
                                        const attachmentItems = Array.isArray(item?.attachments) ? item.attachments : [];

                                        return (
                                            <List.Item
                                                key={`cr-${item?.id ?? createdAt ?? Math.random()}`}
                                                style={{ margin: 0, padding: '0 0 16px 0' }}
                                            >
                                                <CardWithMinHeight
                                                    $padding="16px"
                                                    $minHeight="180px"
                                                    $noHoverShadow
                                                    style={{ height: 500, maxHeight: 500, overflow: 'hidden' }}
                                                >
                                                    <div
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'flex-start',
                                                            justifyContent: 'space-between',
                                                            gap: 8,
                                                            marginBottom: 8,
                                                        }}
                                                    >
                                                        <ContentCardTitle $marginBottom="0" style={{ flex: 1, minWidth: 0 }}>
                                                            Change request
                                                        </ContentCardTitle>
                                                        {item?.status != null && <StatusBadge status={item.status} size="compact" />}
                                                    </div>
                                                    <PanelDivider />
                                                    <div style={{ flex: 1, minHeight: 0, overflowY: 'hidden', paddingRight: 4 }}>
                                                        <MutedText style={{ fontSize: 12 }}>
                                                            Change request {createdAt ? `· ${formatDate(createdAt)}` : ''}
                                                        </MutedText>
                                                        {descriptionMarkdown || attachmentItems.length > 0 ? (
                                                            <>
                                                                <PanelDivider />
                                                                {descriptionMarkdown ? (
                                                                    <Markdown
                                                                        source={String(descriptionMarkdown || '').trim()}
                                                                        variant="compact"
                                                                        linkPrimary
                                                                        richInlineAnchors
                                                                        attachmentItems={attachmentItems}
                                                                    />
                                                                ) : (
                                                                    <div
                                                                        style={{
                                                                            fontSize: 13,
                                                                            color: 'var(--ant-color-text-secondary, rgba(0,0,0,0.55))',
                                                                        }}
                                                                    >
                                                                        —
                                                                    </div>
                                                                )}
                                                                {attachmentItems.length > 0 ? (
                                                                    <div style={{ marginTop: 10 }}>
                                                                        <PhaseDescriptionAttachments
                                                                            items={attachmentItems}
                                                                            style={{ marginTop: 0 }}
                                                                            hideFileNames
                                                                            triggerLabel="Change request attachments"
                                                                        />
                                                                    </div>
                                                                ) : null}
                                                            </>
                                                        ) : null}
                                                    </div>
                                                </CardWithMinHeight>
                                            </List.Item>
                                        );
                                    }}
                                />
                            </div>
                        ))}
                    </div>
                )}
            </Container>
        );
    })();

    const pmoFeedbackView = isClientView
        ? null
        : (() => {
            const completionResponses = (clientFeedbackItems?.completionResponses ?? []).filter(
                (sub) => ['accepted', 'declined', 'change-request'].includes(String(sub?.status ?? '').toLowerCase())
            );
            if (completionResponses.length === 0) {
                return (
                    <EmptyStateBlock>
                        <EmptyStateIcon>
                            <SendOutlined />
                        </EmptyStateIcon>
                        <EmptyStateTitle>No client responses yet</EmptyStateTitle>
                        <EmptyStateDescription>
                            Approved and rejected responses from the client will appear here.
                        </EmptyStateDescription>
                    </EmptyStateBlock>
                );
            }

            return (
                <Container $display="flex" $flexDirection="column" $flex="1" $minHeight="0" style={{ minHeight: 0 }}>
                    <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 12 }}>
                        <List
                            dataSource={completionResponses}
                            grid={{
                                gutter: 16,
                                xs: 1,
                                sm: 1,
                                md: 1,
                                lg: 1,
                                xl: 2,
                                xxl: 3,
                            }}
                            renderItem={(item) => {
                                const status = String(item?.status ?? '').toLowerCase();
                                const isAccepted = status === 'accepted';
                                const isDeclined = status === 'declined';
                                const respondedAt = item?.clientRespondedAt ?? item?.respondedAt ?? item?.updatedAt ?? item?.createdAt;
                                const requestedAt = item?.requestedAt ?? item?.createdAt;
                                const note = item?.clientNote?.markdown ?? item?.clientNote ?? item?.note ?? '';
                                const rating = item?.rating ?? item?.clientRating ?? null;
                                const requestedByName = item?.requestedByName;

                                return (
                                    <List.Item
                                        key={`resp-${item?.id ?? respondedAt ?? Math.random()}`}
                                        style={{ margin: 0, padding: '0 0 16px 0' }}
                                    >
                                        <CardWithMinHeight
                                            $padding="16px"
                                            $minHeight="180px"
                                            $noHoverShadow
                                            style={{
                                                height: 500,
                                                maxHeight: 500,
                                                overflow: 'hidden',
                                            }}
                                        >
                                            <div
                                                role="button"
                                                tabIndex={0}
                                                onClick={() => openClientRequestDetail(item)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        e.preventDefault();
                                                        openClientRequestDetail(item);
                                                    }
                                                }}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'flex-start',
                                                    justifyContent: 'space-between',
                                                    gap: 8,
                                                    marginBottom: 8,
                                                    cursor: 'pointer',
                                                }}
                                            >
                                                <ContentCardTitle $marginBottom="0" style={{ flex: 1, minWidth: 0 }}>
                                                    {getMilestoneDisplayLabel(item?.milestone)}
                                                </ContentCardTitle>
                                                {item?.status != null && <StatusBadge status={item.status} size="compact" />}
                                            </div>

                                            <PanelDivider />

                                            <div
                                                onClick={(e) => e.stopPropagation()}
                                                style={{ flex: 1, minHeight: 0, overflowY: 'hidden', paddingRight: 4 }}
                                            >
                                                <MutedText style={{ fontSize: 12 }}>
                                                    Requested {formatDate(requestedAt) ?? '—'}
                                                </MutedText>
                                                {requestedByName && (
                                                    <>
                                                        <PanelDivider />
                                                        <MutedText style={{ fontSize: 12 }}>Requested by: {requestedByName}</MutedText>
                                                    </>
                                                )}
                                                {(() => {
                                                    const raw =
                                                        item?.completionRequestNotes?.markdown ??
                                                        item?.completionRequestNotes ??
                                                        item?.requestNotes?.markdown ??
                                                        item?.requestNotes ??
                                                        '';
                                                    const text = typeof raw === 'string' ? raw.trim() : '';
                                                    const reqItems = [
                                                        ...(Array.isArray(item?.completionRequestLinks) ? item.completionRequestLinks : []),
                                                        ...(Array.isArray(item?.completionRequestAttachments) ? item.completionRequestAttachments : []),
                                                    ];
                                                    const fallbackReqItems = Array.isArray(item?.requestAttachments) ? item.requestAttachments : [];
                                                    const requestAttachmentItems = reqItems.length > 0 ? reqItems : fallbackReqItems;
                                                    return (
                                                        <>
                                                            <PanelDivider />
                                                            <div
                                                                style={{
                                                                    border: '1px solid var(--ant-color-border-secondary, #f0f0f0)',
                                                                    borderRadius: 10,
                                                                    padding: '8px 10px',
                                                                    background: 'var(--ant-color-fill-quaternary, rgba(0,0,0,0.02))',
                                                                }}
                                                            >
                                                                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
                                                                    <div style={{ minWidth: 0, flex: 1 }}>
                                                                        <div
                                                                            style={{
                                                                                fontSize: 11,
                                                                                fontWeight: 700,
                                                                                letterSpacing: '0.04em',
                                                                                textTransform: 'uppercase',
                                                                                color: 'var(--ant-color-text-secondary, rgba(0,0,0,0.55))',
                                                                                marginBottom: 4,
                                                                                fontFamily: 'var(--font-family-geist)',
                                                                            }}
                                                                        >
                                                                            Request notes
                                                                        </div>
                                                                        {text ? (
                                                                            <div style={{ height: 66, overflow: 'hidden' }}>
                                                                                <Markdown
                                                                                    source={text}
                                                                                    variant="compact"
                                                                                    lineClamp={3}
                                                                                    linkPrimary
                                                                                    richInlineAnchors
                                                                                    attachmentItems={requestAttachmentItems}
                                                                                />
                                                                            </div>
                                                                        ) : (
                                                                            <div style={{ fontSize: 13, color: 'var(--ant-color-text-secondary, rgba(0,0,0,0.55))' }}>—</div>
                                                                        )}
                                                                    </div>
                                                                    {requestAttachmentItems.length > 0 ? (
                                                                        <div style={{ flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                                                                            <PhaseDescriptionAttachments
                                                                                items={requestAttachmentItems}
                                                                                style={{ marginTop: 0 }}
                                                                                hideFileNames
                                                                            />
                                                                        </div>
                                                                    ) : null}
                                                                </div>
                                                            </div>
                                                        </>
                                                    );
                                                })()}

                                                <PanelDivider />

                                                <MutedText style={{ fontSize: 12 }}>
                                                    Response {respondedAt ? `· ${formatDate(respondedAt)}` : ''}
                                                </MutedText>
                                                {typeof rating === 'number' && (
                                                    <>
                                                        <PanelDivider />
                                                        <Rate
                                                            disabled
                                                            value={Math.max(1, Math.min(5, Math.round(rating)))}
                                                            count={5}
                                                            style={{ fontSize: 14 }}
                                                        />
                                                    </>
                                                )}
                                                {note && status !== 'change-request' ? (
                                                    <>
                                                        <PanelDivider />
                                                        <MutedText style={{ fontSize: 12, whiteSpace: 'pre-wrap' }}>{note}</MutedText>
                                                    </>
                                                ) : null}

                                                {Array.isArray(item?.clientAttachments) && item.clientAttachments.length > 0 && (
                                                    <>
                                                        <PanelDivider />
                                                        <PhaseDescriptionAttachments
                                                            items={item.clientAttachments}
                                                            style={{ marginTop: 0 }}
                                                            hideFileNames
                                                            triggerLabel="Response attachments"
                                                        />
                                                    </>
                                                )}

                                                {status === 'change-request' ? (() => {
                                                    const raw = item?.changeRequestNotes?.markdown ?? item?.notes?.markdown ?? item?.notes ?? '';
                                                    const text = typeof raw === 'string' ? raw.trim() : '';
                                                    const items = Array.isArray(item?.changeRequestAttachments)
                                                        ? item.changeRequestAttachments
                                                        : Array.isArray(item?.attachments)
                                                            ? item.attachments
                                                            : [];
                                                    return (
                                                        <>
                                                            <PanelDivider />
                                                            <div
                                                                style={{
                                                                    border: '1px solid var(--ant-color-border-secondary, #f0f0f0)',
                                                                    borderRadius: 10,
                                                                    padding: '8px 10px',
                                                                    background: 'var(--ant-color-fill-quaternary, rgba(0,0,0,0.02))',
                                                                }}
                                                            >
                                                                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
                                                                    <div style={{ minWidth: 0, flex: 1 }}>
                                                                        <div
                                                                            style={{
                                                                                fontSize: 11,
                                                                                fontWeight: 700,
                                                                                letterSpacing: '0.04em',
                                                                                textTransform: 'uppercase',
                                                                                color: 'var(--ant-color-text-secondary, rgba(0,0,0,0.55))',
                                                                                marginBottom: 4,
                                                                                fontFamily: 'var(--font-family-geist)',
                                                                            }}
                                                                        >
                                                                            Change request notes
                                                                        </div>
                                                                        {text ? (
                                                                            <div style={{ height: 66, overflow: 'hidden' }}>
                                                                                <Markdown
                                                                                    source={text}
                                                                                    variant="compact"
                                                                                    lineClamp={3}
                                                                                    linkPrimary
                                                                                    richInlineAnchors
                                                                                    attachmentItems={items}
                                                                                />
                                                                            </div>
                                                                        ) : (
                                                                            <div style={{ fontSize: 13, color: 'var(--ant-color-text-secondary, rgba(0,0,0,0.55))' }}>—</div>
                                                                        )}
                                                                    </div>
                                                                    {items.length > 0 ? (
                                                                        <div style={{ flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                                                                            <PhaseDescriptionAttachments items={items} style={{ marginTop: 0 }} />
                                                                        </div>
                                                                    ) : null}
                                                                </div>
                                                            </div>
                                                        </>
                                                    );
                                                })() : null}
                                            </div>
                                        </CardWithMinHeight>
                                    </List.Item>
                                );
                            }}
                        />
                    </div>
                </Container>
            );
        })();

    return (
        <>
            <Container $display="flex" $flex="1" $minHeight="0" $overflow="hidden">
                <BoardTabRoot
                    style={{
                        flex: 1,
                        minWidth: '100%',
                        minHeight: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    }}
                >
                    <BoardTabRoot
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            flex: 1,
                            minHeight: 0,
                            overflow: 'hidden',
                            '--board-kanban-inline-gutter': 'clamp(16px, 4vw, 40px)',
                        }}
                    >
                        <Container
                            $display="flex"
                            $justifyContent="space-between"
                            $alignItems="flex-start"
                            $gap="16px"
                            $flexWrap="wrap"
                            $marginBottom="16px"
                        >
                            <Container>
                                <BoardPageTitle>{isClientView ? 'Delivery' : 'PMO'}</BoardPageTitle>
                                <BoardPageSubtitle>
                                    Submissions, client delivery, and feedback in one place.
                                </BoardPageSubtitle>
                            </Container>
                            <Container $display="flex" $alignItems="center" $gap="8px" $flexWrap="wrap" />
                        </Container>

                        {mainTabs}

                        {isLoading ? (
                            <EmptyStateBlock>
                                <DetailPanelLoading>
                                    <Spin size="large" />
                                </DetailPanelLoading>
                            </EmptyStateBlock>
                        ) : pmoViewTab === 'submission' ? (
                            isClientView ? (
                                clientRequestsList
                            ) : total === 0 ? (
                                <>
                                    <MutedText style={{ fontSize: 14, marginBottom: 16 }}>
                                        No PMO submissions yet. When the team submits work to PMO (e.g. from QA or
                                        Sprints), it will appear here.
                                    </MutedText>
                                    <BoardKanbanBoardShell>
                                        <Container
                                            $display="flex"
                                            $flexDirection="column"
                                            $flex="1"
                                            $minHeight="0"
                                            $overflow="hidden"
                                            style={{ width: '100%' }}
                                        >
                                            <PMOKanbanBoard
                                                submissionRows={[]}
                                                submittedToClientRows={[]}
                                                submissionDragEnabled={false}
                                                onSubmissionOpen={handleSubmissionKanbanOpen}
                                                onTaskFromSubmission={handleTaskFromSubmissionKanban}
                                                onOpenSubmittedClientSubmission={handleSubmittedClientKanbanOpen}
                                                onTaskFromSubmittedClient={handleTaskFromSubmittedClientKanban}
                                                dropTarget={dropTarget}
                                                draggingColumn={draggingColumn}
                                                onDndDragStart={handleDndDragStart}
                                                onDndDragOverColumn={handleDndDragOverColumn}
                                                onDndDragEnd={handleDndDragEnd}
                                            />
                                        </Container>
                                    </BoardKanbanBoardShell>
                                </>
                            ) : (
                                <>
                                    <BoardKanbanBoardShell>
                                        <Container
                                            $display="flex"
                                            $flexDirection="column"
                                            $flex="1"
                                            $minHeight="0"
                                            $overflow="hidden"
                                            style={{ width: '100%' }}
                                        >
                                            <PMOKanbanBoard
                                                submissionRows={submissionColumn}
                                                submittedToClientRows={submittedToClientColumn}
                                                submissionDragEnabled={canSubmitToClient}
                                                onSubmissionOpen={handleSubmissionKanbanOpen}
                                                onTaskFromSubmission={handleTaskFromSubmissionKanban}
                                                onOpenSubmittedClientSubmission={handleSubmittedClientKanbanOpen}
                                                onTaskFromSubmittedClient={handleTaskFromSubmittedClientKanban}
                                                dropTarget={dropTarget}
                                                draggingColumn={draggingColumn}
                                                onDndDragStart={handleDndDragStart}
                                                onDndDragOverColumn={handleDndDragOverColumn}
                                                onDndDragEnd={handleDndDragEnd}
                                            />
                                        </Container>
                                    </BoardKanbanBoardShell>
                                </>
                            )
                        ) : isClientView ? (
                            clientFeedbackView
                        ) : (
                            pmoFeedbackView
                        )}
                    </BoardTabRoot>
                </BoardTabRoot>
            </Container>

            {!isClientView && (
                <>
                    <PmoSubmitToClientModal
                        open={submitModalOpen}
                        onCancel={closeSubmitModal}
                        sidLabel={submitModalPresentation.sidLabel}
                        presetHeading={submitModalPresentation.presetHeading}
                        descriptionHtml={notes}
                        onDescriptionChange={setNotes}
                        submissionDescriptionRaw={submitModalSubmissionDescriptionRaw}
                        detailSections={submitModalDetailSections}
                        taskRows={pmoSubmitModalTaskRows}
                        onTaskRowClick={(payload) => {
                            if (!payload?.task) return;
                            handleTaskSelect(payload.sprint, payload.task);
                        }}
                        onSubmit={() =>
                            handleSubmitToClient({
                                onSuccess: () => message.success('Submitted to client'),
                                onError: (err) =>
                                    message.error(
                                        err?.response?.data?.message || err?.message || 'Failed to submit to client',
                                    ),
                            })
                        }
                        submitPending={submitToClientPending}
                        submitDisabled={submitToClientPending || !isSubmissionPending(selectedSubmission)}
                        submitPermissionDenied={!canSubmitToClient}
                        afterDescription={submitModalAfterDescription}
                        modalSize="md"
                        mentionUsers={mentionUsers}
                        mentionMilestones={mentionMilestones}
                        mentionSprints={mentionSprints}
                        richTextEditorProps={{
                            showAttachButton: true,
                            onFilesSelected: handleFilesSelected,
                            onLinkRequest: handleLinkRequest,
                            onAttachmentRemoved: (index) => {
                                const p = pendingFiles[index];
                                if (p) handleRemoveAttachment(p.id);
                            },
                        }}
                    />

                    <PmoSubmitToClientModal
                        open={submissionDetailModalOpen}
                        onCancel={closeSubmissionDetailModal}
                        viewOnly
                        sidLabel={submissionDetailPresentation.sidLabel}
                        presetHeading={submissionDetailPresentation.presetHeading}
                        submissionDescriptionRaw={undefined}
                        taskRows={pmoSubmissionDetailTaskRows}
                        viewLinks={submissionDetailViewLinks}
                        viewAttachments={submissionDetailViewAttachments}
                        viewClientAttachments={submissionDetailViewClientAttachments}
                        detailSections={submissionDetailSections}
                        onTaskRowClick={(payload) => {
                            if (!payload?.task) return;
                            handleTaskSelect(payload.sprint, payload.task);
                        }}
                        onSubmit={() => { }}
                        submitPending={false}
                        submitDisabled
                        submitPermissionDenied={false}
                        modalSize="md"
                        mentionUsers={mentionUsers}
                        mentionMilestones={mentionMilestones}
                        mentionSprints={mentionSprints}
                    />

                    <MilestoneLinkModal
                        open={linkModalOpen}
                        onCancel={closeLinkModal}
                        linkModalForm={linkForm}
                        handleLinkModalSubmit={handleLinkModalSubmit}
                    />
                </>
            )}

            {isClientView && (
                <>
                    <PmoSubmitToClientModal
                        open={clientRequestDetailOpen}
                        onCancel={closeClientRequestDetail}
                        viewOnly
                        sidLabel={clientRequestDetailPresentation?.sidLabel}
                        presetHeading={clientRequestDetailPresentation?.presetHeading}
                        submissionDescriptionRaw={undefined}
                        taskRows={[]}
                        onTaskRowClick={undefined}
                        viewLinks={[]}
                        viewAttachments={clientRequestDetailViewAttachments}
                        viewClientAttachments={clientRequestDetailViewClientAttachments}
                        detailSections={clientRequestDetailSections}
                        descriptionSectionLabel="Client notes"
                        onSubmit={() => { }}
                        submitPending={false}
                        submitDisabled
                        submitPermissionDenied={false}
                        modalSize="md"
                        footerOverride={
                            String(clientRequestDetail?.status || '').toLowerCase() === 'pending' ? (
                                <CreateRoleModalFooter $inFooterSlot>
                                    <Container
                                        $display="flex"
                                        $justifyContent="space-between"
                                        $alignItems="center"
                                        $gap="12px"
                                        style={{ width: '100%' }}
                                    >
                                        <Button $primary={false} $size="14px" onClick={closeClientRequestDetail}>
                                            Close
                                        </Button>
                                        <Container
                                            $display="flex"
                                            $gap="8px"
                                            $flexWrap="nowrap"
                                            style={{ alignItems: 'center' }}
                                        >
                                            <Button
                                                type="button"
                                                $dangerOutline
                                                $primary={false}
                                                $size="14px"
                                                icon={<CloseOutlined />}
                                                onClick={() => openRespondModal(clientRequestDetail?.id, 'declined')}
                                            >
                                                Reject
                                            </Button>
                                            <Button
                                                type="button"
                                                $primary
                                                $blackOnLight
                                                $size="14px"
                                                icon={<CheckOutlined />}
                                                onClick={() => openRespondModal(clientRequestDetail?.id, 'accepted')}
                                            >
                                                Approve
                                            </Button>
                                        </Container>
                                    </Container>
                                </CreateRoleModalFooter>
                            ) : (
                                <CreateRoleModalFooter $inFooterSlot>
                                    <Button $primary={false} $size="14px" onClick={closeClientRequestDetail}>
                                        Close
                                    </Button>
                                </CreateRoleModalFooter>
                            )
                        }
                    />
                    <AppModal
                        size="md"
                        title={respondAction === 'accepted' ? 'Approve request' : 'Response'}
                        open={respondModalOpen}
                        onCancel={closeRespondModal}
                        footer={null}
                    >
                        <CreateRoleModalBody>
                            {respondAction === 'declined' ? (
                                <>
                                    <RolesSliderTabWrap style={{ marginBottom: 16 }}>
                                        <SliderTab
                                            items={[
                                                { key: 'backlog', label: 'Backlog' },
                                                { key: 'change_request', label: 'Change request' },
                                            ]}
                                            activeKey={respondResponseTab}
                                            onChange={setRespondResponseTab}
                                            fitContent
                                        />
                                    </RolesSliderTabWrap>

                                    {respondResponseTab === 'backlog' ? (
                                        <>
                                            <Form layout="vertical">
                                                <Form.Item label="Reason">
                                                    <RichTextEditor
                                                        placeholder="Explain why you are rejecting (markdown, attachments, links)…"
                                                        value={respondNote}
                                                        onChange={setRespondNote}
                                                        showAttachButton
                                                        onFilesSelected={handleRespondFilesSelected}
                                                        onLinkRequest={handleRespondLinkRequest}
                                                        onLinkRemoved={handleRespondEditorLinkRemoved}
                                                        attachmentIndexOffset={respondPendingFiles.length}
                                                        onAttachmentRemoved={(index) => {
                                                            const p = respondPendingFiles[index];
                                                            if (p) handleRespondRemoveAttachment(p.id);
                                                        }}
                                                    />
                                                </Form.Item>
                                            </Form>
                                            {respondPendingLinks.length > 0 && (
                                                <Container
                                                    $display="flex"
                                                    $flexDirection="column"
                                                    $marginTop="8px"
                                                    $marginBottom="14px"
                                                >
                                                    <PendingLinkWrap links={respondPendingLinks} onRemoveLink={handleRespondRemoveLink} />
                                                </Container>
                                            )}
                                            {respondPendingFiles.length > 0 && (
                                                <Container
                                                    $display="flex"
                                                    $flexDirection="column"
                                                    $marginTop={respondPendingLinks.length > 0 ? '0' : '8px'}
                                                    $marginBottom="16px"
                                                    $padding="8px 0"
                                                    $borderTop="1px solid var(--ant-color-border, #f0f0f0)"
                                                >
                                                    <PanelSectionLabel
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 6,
                                                            marginBottom: 8,
                                                            fontSize: 11,
                                                            textTransform: 'uppercase',
                                                            letterSpacing: '0.04em',
                                                            opacity: 0.75,
                                                        }}
                                                    >
                                                        <FileOutlined />
                                                        Attachments
                                                    </PanelSectionLabel>
                                                    <div
                                                        style={{
                                                            display: 'grid',
                                                            gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
                                                            gap: 10,
                                                        }}
                                                    >
                                                        {respondPendingFiles.map((p) => (
                                                            <PendingAttachmentTile
                                                                key={p.id}
                                                                item={p}
                                                                onRemove={() => handleRespondRemoveAttachment(p.id)}
                                                                truncateFileName={truncateFileName}
                                                                showBottomFileName={false}
                                                            />
                                                        ))}
                                                    </div>
                                                </Container>
                                            )}
                                        </>
                                    ) : (
                                        <>
                                            <Form form={changeRequestForm} layout="vertical">
                                                <Form.Item
                                                    name="description"
                                                    label="Description"
                                                >
                                                    <RichTextEditor
                                                        submissionNeutralLight
                                                        value={changeRequestDescriptionValue}
                                                        onChange={(next) => changeRequestForm.setFieldValue('description', next)}
                                                        isAttachment
                                                        isLink
                                                        isMention
                                                        placeholder="Describe what needs to change — markdown, links, and attachments."
                                                        showAttachButton
                                                        onFilesSelected={handleChangeRequestFilesSelected}
                                                        onLinkRequest={handleChangeRequestLinkRequest}
                                                        onLinkRemoved={handleChangeRequestEditorLinkRemoved}
                                                        attachmentIndexOffset={changeRequestPendingFiles.length}
                                                        onAttachmentRemoved={(index) => {
                                                            const p = changeRequestPendingFiles[index];
                                                            if (p) handleChangeRequestRemoveAttachment(p.id);
                                                        }}
                                                    />
                                                </Form.Item>
                                            </Form>
                                            {changeRequestPendingLinks.length > 0 && (
                                                <Container
                                                    $display="flex"
                                                    $flexDirection="column"
                                                    $marginTop="8px"
                                                    $marginBottom="14px"
                                                >
                                                    <PendingLinkWrap
                                                        links={changeRequestPendingLinks}
                                                        onRemoveLink={handleChangeRequestRemoveLink}
                                                    />
                                                </Container>
                                            )}
                                            {changeRequestPendingFiles.length > 0 && (
                                                <Container
                                                    $display="flex"
                                                    $flexDirection="column"
                                                    $marginTop={changeRequestPendingLinks.length > 0 ? '0' : '8px'}
                                                    $marginBottom="16px"
                                                    $padding="8px 0"
                                                    $borderTop="1px solid var(--ant-color-border, #f0f0f0)"
                                                >
                                                    <PanelSectionLabel
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 6,
                                                            marginBottom: 8,
                                                            fontSize: 11,
                                                            textTransform: 'uppercase',
                                                            letterSpacing: '0.04em',
                                                            opacity: 0.75,
                                                        }}
                                                    >
                                                        <FileOutlined />
                                                        Attachments
                                                    </PanelSectionLabel>
                                                    <div
                                                        style={{
                                                            display: 'grid',
                                                            gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
                                                            gap: 10,
                                                        }}
                                                    >
                                                        {changeRequestPendingFiles.map((p) => (
                                                            <PendingAttachmentTile
                                                                key={p.id}
                                                                item={p}
                                                                onRemove={() => handleChangeRequestRemoveAttachment(p.id)}
                                                                truncateFileName={truncateFileName}
                                                                showBottomFileName={false}
                                                            />
                                                        ))}
                                                    </div>
                                                </Container>
                                            )}
                                        </>
                                    )}
                                </>
                            ) : (
                                <>
                                    <Form layout="vertical">
                                        <Form.Item label="Rating">
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    gap: 12,
                                                }}
                                            >
                                                <Rate
                                                    count={5}
                                                            value={
                                                                typeof respondRating === 'number' && !Number.isNaN(respondRating)
                                                                    ? Math.max(1, Math.min(5, Math.round(respondRating)))
                                                                    : 0
                                                            }
                                                            onChange={(next) => {
                                                                if (typeof next !== 'number' || Number.isNaN(next)) {
                                                                    setRespondRating(next);
                                                                    return;
                                                                }
                                                                setRespondRating(Math.max(1, Math.min(5, Math.round(next))));
                                                            }}
                                                />
                                                        <div style={{ fontSize: 12, opacity: 0.75, whiteSpace: 'nowrap' }}>
                                                            {typeof respondRating === 'number' && !Number.isNaN(respondRating)
                                                                ? Math.max(1, Math.min(5, Math.round(respondRating)))
                                                                : 0}
                                                </div>
                                            </div>
                                        </Form.Item>
                                        <Form.Item label="Note">
                                            <RichTextEditor
                                                placeholder="Add a note (markdown, attachments, links)…"
                                                value={respondNote}
                                                onChange={setRespondNote}
                                                showAttachButton
                                                onFilesSelected={handleRespondFilesSelected}
                                                onLinkRequest={handleRespondLinkRequest}
                                                onLinkRemoved={handleRespondEditorLinkRemoved}
                                                attachmentIndexOffset={respondPendingFiles.length}
                                                onAttachmentRemoved={(index) => {
                                                    const p = respondPendingFiles[index];
                                                    if (p) handleRespondRemoveAttachment(p.id);
                                                }}
                                            />
                                        </Form.Item>
                                    </Form>
                                    {respondPendingLinks.length > 0 && (
                                        <Container $display="flex" $flexDirection="column" $marginTop="8px" $marginBottom="14px">
                                            <PendingLinkWrap links={respondPendingLinks} onRemoveLink={handleRespondRemoveLink} />
                                        </Container>
                                    )}
                                    {respondPendingFiles.length > 0 && (
                                        <Container
                                            $display="flex"
                                            $flexDirection="column"
                                            $marginTop={respondPendingLinks.length > 0 ? '0' : '8px'}
                                            $marginBottom="16px"
                                            $padding="8px 0"
                                            $borderTop="1px solid var(--ant-color-border, #f0f0f0)"
                                        >
                                            <PanelSectionLabel
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 6,
                                                    marginBottom: 8,
                                                    fontSize: 11,
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.04em',
                                                    opacity: 0.75,
                                                }}
                                            >
                                                <FileOutlined />
                                                Attachments
                                            </PanelSectionLabel>
                                            <div
                                                style={{
                                                    display: 'grid',
                                                    gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
                                                    gap: 10,
                                                }}
                                            >
                                                {respondPendingFiles.map((p) => (
                                                    <PendingAttachmentTile
                                                        key={p.id}
                                                        item={p}
                                                        onRemove={() => handleRespondRemoveAttachment(p.id)}
                                                        truncateFileName={truncateFileName}
                                                        showBottomFileName={false}
                                                    />
                                                ))}
                                            </div>
                                        </Container>
                                    )}
                                </>
                            )}
                        </CreateRoleModalBody>
                        <CreateRoleModalFooter>
                            <Button $primary={false} $size="14px" onClick={closeRespondModal}>
                                Cancel
                            </Button>
                            <Button
                                $primary
                                $blackOnLight
                                $size="14px"
                                icon={
                                    respondAction === 'accepted'
                                        ? <CheckOutlined />
                                        : respondResponseTab === 'change_request'
                                            ? <SendOutlined />
                                            : <CloseOutlined />
                                }
                                loading={respondResponseTab === 'change_request' ? createChangeRequestPending : respondPending}
                                // permissionDenied={respondResponseTab === 'change_request' ? !canCreateChangeRequest : !canRespondToRequest}
                                onClick={() => {
                                    if (respondAction === 'declined' && respondResponseTab === 'change_request') {
                                        submitChangeRequest({
                                            onSuccess: () => {
                                                closeRespondModal();
                                                message.success('Change request sent');
                                            },
                                            onError: (err) =>
                                                message.error(
                                                    err?.response?.data?.message ||
                                                    err?.message ||
                                                    'Failed to send change request',
                                                ),
                                        });
                                        return;
                                    }

                                    handleRespondSubmit({
                                        onSuccess: () =>
                                            message.success(
                                                respondAction === 'accepted' ? 'Request approved' : 'Request rejected',
                                            ),
                                        onError: (err) =>
                                            message.error(
                                                err?.response?.data?.message || err?.message || 'Failed to respond',
                                            ),
                                    });
                                }}
                            >
                                {respondAction === 'accepted'
                                    ? 'Approve'
                                    : respondResponseTab === 'change_request'
                                        ? 'Send'
                                        : 'Backlog'}
                            </Button>
                        </CreateRoleModalFooter>
                    </AppModal>
                    <MilestoneLinkModal
                        open={respondLinkModalOpen}
                        onCancel={closeRespondLinkModal}
                        linkModalForm={respondLinkForm}
                        handleLinkModalSubmit={handleRespondLinkModalSubmit}
                    />
                    <MilestoneLinkModal
                        open={changeRequestLinkModalOpen}
                        onCancel={closeChangeRequestLinkModal}
                        linkModalForm={changeRequestLinkForm}
                        handleLinkModalSubmit={handleChangeRequestLinkModalSubmit}
                    />
                </>
            )}

            <TaskDetailModal
                projectId={projectId}
                open={Boolean(selectedTask?.sprint && selectedTask?.task)}
                task={selectedTask?.task ?? null}
                taskDescriptionMarkdown={taskDescriptionMarkdown}
                taskBugs={pmoTaskDetailBugs}
                onClose={closeTaskPanel}
                onRequestRefresh={() => {
                    if (selectedTask?.sprint && selectedTask?.task) {
                        // Reuse PMO hook hydration to fetch latest task details + bugs.
                        void hydrateTaskDetail(selectedTask.sprint, selectedTask.task);
                    }
                }}
                readOnlyBugActions
            />
        </>
    );
}
